// src/pages/BookingConfirmation/BookingConfirmation.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2, Clock, MapPin, User, CreditCard, Calendar, Info } from 'lucide-react';
import { motion } from 'framer-motion';

const BookingConfirmation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const bookingData = location.state?.bookingData;

  useEffect(() => {
    if (!bookingData) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(`/order/${bookingData.orderId}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [bookingData, navigate]);

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-sm w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8"
        >
          <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600 text-sm sm:text-base">No booking information found.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm hover:bg-emerald-700 transition-colors"
          >
            Go Home
          </button>
        </motion.div>
      </div>
    );
  }

  // Format price with proper currency
  const formattedPrice = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(bookingData.price || 0);

  const formattedCommitmentFee = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format((bookingData.price || 0) * 0.1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-3 sm:p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl overflow-hidden max-w-md w-full mx-auto"
      >
        {/* Success Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-4 sm:px-6 py-6 sm:py-8 text-center">
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg"
          >
            <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600" />
          </motion.div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">Booking Confirmed!</h1>
          <p className="text-emerald-100 text-xs sm:text-sm">
            Your order has been created successfully
          </p>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Payment Alert */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-2 sm:gap-3">
              <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs sm:text-sm font-semibold text-amber-800">Payment Required</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Please pay the <span className="font-bold">{formattedCommitmentFee}</span> commitment fee to proceed.
                </p>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="space-y-3 sm:space-y-4">
            {/* Order ID */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-xs sm:text-sm text-gray-500">Order ID</span>
              <span className="text-xs sm:text-sm font-mono font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">
                {bookingData.orderId}
              </span>
            </div>

            {/* Rider Info */}
            <div className="flex items-center gap-3 sm:gap-4 py-2 border-b border-gray-100">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-sm sm:text-base flex-shrink-0">
                {bookingData.riderName?.charAt(0) || 'R'}
              </div>
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-500">Rider</p>
                <p className="text-sm sm:text-base font-semibold text-gray-900">{bookingData.riderName || 'Rider'}</p>
              </div>
            </div>

            {/* Pickup Location */}
            <div className="flex items-start gap-2 sm:gap-3 py-2 border-b border-gray-100">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-500">Pickup Location</p>
                <p className="text-sm sm:text-base font-medium text-gray-900 break-words">{bookingData.pickupLocation}</p>
              </div>
            </div>

            {/* Destination */}
            <div className="flex items-start gap-2 sm:gap-3 py-2 border-b border-gray-100">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-500">Destination</p>
                <p className="text-sm sm:text-base font-medium text-gray-900 break-words">{bookingData.destination}</p>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="mt-2 p-3 sm:p-4 bg-gray-50 rounded-xl">
              <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Price Breakdown</p>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600">Trip Fee</span>
                  <span className="text-xs sm:text-sm font-medium">{formattedPrice}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600">Platform Fee</span>
                  <span className="text-xs sm:text-sm font-medium text-amber-600">Included</span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm font-semibold text-gray-900">Total Amount</span>
                    <span className="text-base sm:text-lg font-bold text-emerald-600">{formattedPrice}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">You pay now (10%)</span>
                    <span className="text-sm font-bold text-emerald-600">{formattedCommitmentFee}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">Pay after trip (90%)</span>
                    <span className="text-sm font-medium text-gray-700">
                      {new Intl.NumberFormat('en-NG', {
                        style: 'currency',
                        currency: 'NGN',
                        minimumFractionDigits: 0
                      }).format((bookingData.price || 0) * 0.9)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Redirect Timer */}
          <div className="mt-4 sm:mt-6 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500 mb-3 sm:mb-4">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <p className="text-xs sm:text-sm">Redirecting in <span className="font-bold text-emerald-600">{countdown}</span> seconds...</p>
            </div>
            
            <button
              onClick={() => navigate(`/order/${bookingData.orderId}`)}
              className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-green-700 flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
            >
              Go to Order Now
              <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>

          {/* Info Note */}
          <div className="mt-4 sm:mt-6 p-2.5 sm:p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] sm:text-xs text-blue-700">
                You'll be redirected to the order page where you can complete the payment. 
                The commitment fee is refundable if the rider cancels.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BookingConfirmation;